import { USER_DELIVERY_STORE_KEY } from '//99bikes.com.au/cdn/shop/t/354/assets/99-geolocation.js?manual=20260827&v=16233740355998488301793310994';

/**
 * @file: assets/store-locator-index.js
 * @purpose: Provides functionality for the store locator feature
 * @date: 2023-12-01
 */

const selectors = {
  radioInputs: '[data-region-handle]',
  region: '[data-region]',
  storeList: '[data-store-list]',
};

class StoreLocatorIndex extends HTMLElement {
  constructor() {
    super();

    this.stores = {}; //populate stores, sorted by region, on init
    this.handleNavItemClick = this.handleNavItemClick.bind(this);
    this.panOnMap = false; //20261001 -PO, for initial automated region selection, unless provided in the URL, don't pan on the map
    this.init();
  }

  init() {
    // Cache DOM elements
    this.elements = {
      regionTitle: this.querySelector(selectors.region),
      storeList: this.querySelector(selectors.storeList),
    };

    this.attachEventListeners();
    this.loadStores();

    //if region not found, wait for store to be selected before selecting region
    if (this.regionNotFound) {
      this.storeUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.deliveryStoreUpdated,
        this.onStoreUpdated.bind(this)
      );
    }
  }

  async loadStores() {
    //20260904 -PO, load in stores from HTML once, then show / hide when region selected;
    if (!this.elements.storeList) {
      console.error('Failed to find store list.');
      return;
    }

    for (const store of this.elements.storeList.childNodes) {
      if (!store.dataset) continue; //ignore non-html blocks
      const region = store.dataset.storeRegionName;
      if (!region) {
        console.error(`Failed to find store region for store:`,store);
        continue; //do not hide store, instead display for all regions so it can more quickly be identified 
      }
      if (!this.stores[region]) this.stores[region] = [];
      this.stores[region].push(store);
    }

    //get selected region
    const params = new URLSearchParams(window.location.search);
    const selectedRegionHandle = params.get('region');
    if (selectedRegionHandle) {
      this.panOnMap = true;
      const regionSelected = this.selectRegion(selectedRegionHandle);
      if (regionSelected) return;
      //reset if no region found
      this.panOnMap = false;
    }

    const regions = JSON.parse(this.dataset.storeRegions || null);
    if (!regions || regions.length <= 0) return;

    //get the users current region (from their selected store)
    const currentRegion = this.getCurrentRegion();
    if (currentRegion && regions.includes(currentRegion)) {
      this.selectRegion(null,currentRegion);
      return;
    }

    this.regionNotFound = true;
  }

  onStoreUpdated() {
    if (!this.regionNotFound) return;

    const regions = JSON.parse(this.dataset.storeRegions || null);
    if (!regions || regions.length <= 0) return;

    const currentRegion = this.getCurrentRegion();
    if (currentRegion && regions.includes(currentRegion)) {
      this.selectRegion(null,currentRegion);
      this.regionNotFound = false;
    }
  }

  getCurrentRegion() {
    //20260904 -PO, will attempt to get region from the selected C&C location
    //note: will not work for new users landing directly on the store-locator page, as the
    //process to populate the store will finish after this
    return JSON.parse(localStorage.getItem(USER_DELIVERY_STORE_KEY) || null)?.area_location;
  }

  //will check from handle if given, else will check by title
  selectRegion(handle,title = null) {
    const selector = handle ? `[data-region-handle="${handle}"]` : `[data-region-title="${title}"]`;
    const regionInput = document.querySelector(selector);
    if (!regionInput) return false;
    regionInput.click();
    return true;
  }

  attachEventListeners() {
    const radioInputs = this.querySelectorAll(selectors.radioInputs);
    if (!radioInputs) return;

    radioInputs.forEach(radioInput => {
      radioInput.addEventListener('change', this.handleNavItemClick);
    });
  }

  handleNavItemClick(event) {
    const { regionTitle, regionHandle } = event.target.dataset;

    this.populateStoreList(regionTitle);

    if (this.panOnMap) {
      //Url selected region
      const url = new URL(window.location);
      url.searchParams.set('region',regionHandle);
      history.replaceState({}, '', url);

      this.selectRegionStockist(regionTitle);
    } else {
      //ensure any following (user) clicks pan on map
      this.panOnMap = true;
    }
  }

  populateStoreList(regionTitle) {
    if (this.elements.regionTitle) {
      this.elements.regionTitle.textContent = regionTitle || 'All';
    }

    for (const region of Object.keys(this.stores)) {
      const show = region === regionTitle;
      for (const store of this.stores[region]) {
        if (show) {
          store.style.removeProperty('display');
        } else {
          store.style.setProperty('display','none');
        }
      }
    }
  }

  selectRegionStockist(regionName) {
    window.stockistStartingQuery = regionName; //in case map has not yet loaded
    window.stockistSubmitSearch?.(regionName);
  }

  disconnectedCallback() {
    const radioInputs = this.querySelectorAll(selectors.radioInputs);
    if (!radioInputs) return;

    radioInputs.forEach(radioInput => {
      radioInput.removeEventListener('change', this.handleNavItemClick);
    });

    this.storeUpdateUnsubscriber?.();
  }
}

// Define the custom element
customElements.define('store-locator-index', StoreLocatorIndex);
